import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Clock, Play, Search, Calendar, Filter, BarChart3 } from "lucide-react";
import { getCurrentUser } from "@/lib/auth";
import { getViewHistory, getVideos, getCategories, getModules, getVideoProgress as getVideoProgressApi } from "@/lib/storage";
import { ViewHistory, Video, Category } from "@/types";
import { formatDurationLong } from '@/lib/utils';

export default function History() {
  const navigate = useNavigate();
  const [currentUser] = useState(getCurrentUser());
  const [history, setHistory] = useState<ViewHistory[]>([]);
  const [videos, setVideos] = useState<Video[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [modulesByCategory, setModulesByCategory] = useState<Record<string, any[]>>({});
  const [videoProgressById, setVideoProgressById] = useState<Record<string, { duration: number; currentTime: number; completed: boolean }>>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  // Item 4 do documento: evitar lista longa numa página só
  const [pagina, setPagina] = useState(1);
  const porPagina = 12;
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      navigate("/login");
      return;
    }

    (async () => {
      try {
        const [userHistory, videosList, categoriesList] = await Promise.all([
          getViewHistory(currentUser.id),
          getVideos(),
          getCategories(),
        ]);
        setHistory(userHistory);
        setVideos(videosList);
        setCategories(categoriesList);
        const modMap: Record<string, any[]> = {};
        for (const c of categoriesList) {
          const mods = await getModules(c.id);
          modMap[c.id] = mods;
        }
        setModulesByCategory(modMap);

        // Carregar progresso/duração para vídeos com duração desconhecida (0)
        const unknownDurationVideoIds = Array.from(new Set(userHistory
          .map(h => h.videoId)
          .filter(vid => (videosList.find(v => v.id === vid)?.duration || 0) <= 0)));
        if (unknownDurationVideoIds.length > 0) {
          const entries = await Promise.all(unknownDurationVideoIds.map(async (vid) => {
            try {
              const vp = await getVideoProgressApi(vid);
              return [vid, vp] as const;
            } catch {
              return [vid, null] as const;
            }
          }));
          const map: Record<string, { duration: number; currentTime: number; completed: boolean }> = {};
          for (const [vid, vp] of entries) {
            if (vp) {
              map[vid] = { duration: Math.max(0, vp.duration || 0), currentTime: Math.max(0, vp.currentTime || 0), completed: !!vp.completed };
            }
          }
          setVideoProgressById(map);
        }
      } catch (_e) {
        setHistory([]);
        setVideos([]);
        setCategories([]);
      }
    })();
  }, [currentUser, navigate]);

  const getVideo = (videoId: string) => {
    return videos.find(v => v.id === videoId);
  };

  const getCategory = (categoryId: string) => {
    return categories.find(c => c.id === categoryId);
  };

  const formatDate = (date: Date) => {
    const now = new Date();
    const diffInMs = now.getTime() - new Date(date).getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return "Hoje";
    if (diffInDays === 1) return "Ontem";
    if (diffInDays < 7) return `${diffInDays} dias atrás`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} semanas atrás`;
    if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} meses atrás`;
    return `${Math.floor(diffInDays / 365)} anos atrás`;
  };

  const paginar = <T,>(lista: T[]) => {
    const total = Math.max(1, Math.ceil(lista.length / porPagina));
    const atual = Math.min(pagina, total);
    return { total, atual, itens: lista.slice((atual - 1) * porPagina, atual * porPagina) };
  };

  const filteredAndSortedHistory = useMemo(() => {
    let filtered = [...history];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(item => {
        const video = getVideo(item.videoId);
        return video?.title.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    // Category filter
    if (filterCategory !== "all") {
      filtered = filtered.filter(item => {
        const video = getVideo(item.videoId);
        return video?.categoryId === filterCategory;
      });
    }

    // Status filter
    if (filterStatus === "completed") {
      filtered = filtered.filter(item => item.completed);
    } else if (filterStatus === "inProgress") {
      filtered = filtered.filter(item => !item.completed);
    }

    // Sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "recent":
          return new Date(b.lastWatchedAt).getTime() - new Date(a.lastWatchedAt).getTime();
        case "oldest":
          return new Date(a.lastWatchedAt).getTime() - new Date(b.lastWatchedAt).getTime();
        case "progress":
          const videoA = getVideo(a.videoId);
          const videoB = getVideo(b.videoId);
          const progressA = videoA && videoA.duration > 0 ? (a.watchedDuration / videoA.duration) * 100 : (a.completed ? 100 : 0);
          const progressB = videoB && videoB.duration > 0 ? (b.watchedDuration / videoB.duration) * 100 : (b.completed ? 100 : 0);
          return progressB - progressA;
        default:
          return 0;
      }
    });

    return filtered;
  }, [history, searchTerm, filterCategory, filterStatus, sortBy]);

  // Statistics
  const stats = useMemo(() => {
    const totalWatched = history.length;
    const completedVideos = history.filter(h => h.completed).length;
    const totalWatchTime = history.reduce((acc, h) => acc + h.watchedDuration, 0);

    let sumPercent = 0;
    let count = 0;
    for (const h of history) {
      const video = getVideo(h.videoId);
      if (video && video.duration > 0) {
        sumPercent += (h.watchedDuration / video.duration) * 100;
        count += 1;
      } else {
        // Se não temos duração, usar completed como 100%, senão 0%
        sumPercent += h.completed ? 100 : 0;
        count += 1;
      }
    }
    const avg = count > 0 ? sumPercent / count : 0;
    const averageCompletion = Number.isFinite(avg) ? avg : 0;

    return {
      totalWatched,
      completedVideos,
      totalWatchTime,
      averageCompletion
    };
  }, [history]);

  const handleContinueWatching = (videoId: string) => {
    navigate(`/video/${videoId}`);
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <div className="flex flex-col gap-4">
          <h1 className="text-3xl font-bold">Histórico de Visualização</h1>
          <p className="text-muted-foreground">
            Acompanhe seu progresso e continue de onde parou
          </p>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Vídeos Assistidos
              </CardTitle>
              <Play className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalWatched}</div>
              <p className="text-xs text-muted-foreground">
                Total de vídeos visualizados
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Concluídos
              </CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.completedVideos}</div>
              <p className="text-xs text-muted-foreground">
                Vídeos assistidos até o fim
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Tempo Total
              </CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatDurationLong(stats.totalWatchTime)}
              </div>
              <p className="text-xs text-muted-foreground">
                Tempo total assistido
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Progresso Médio
              </CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats.averageCompletion.toFixed(0)}%
              </div>
              <p className="text-xs text-muted-foreground">
                Média de conclusão
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filtros
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Buscar vídeo..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPagina(1); }}
                  className="pl-10"
                />
              </div>

              <Select value={filterCategory} onValueChange={(v) => { setFilterCategory(v); setPagina(1); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as Categorias</SelectItem>
                  {categories.map(category => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); setPagina(1); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="completed">Concluídos</SelectItem>
                  <SelectItem value="inProgress">Em Progresso</SelectItem>
                </SelectContent>
              </Select>

              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger>
                  <SelectValue placeholder="Ordenar por" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recent">Mais Recentes</SelectItem>
                  <SelectItem value="oldest">Mais Antigos</SelectItem>
                  <SelectItem value="progress">Maior Progresso</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* History List */}
        <div className="space-y-4">
          {filteredAndSortedHistory.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Clock className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">Nenhum histórico encontrado</h3>
                <p className="text-muted-foreground text-center">
                  {searchTerm || filterCategory !== "all" || filterStatus !== "all"
                    ? "Tente ajustar os filtros"
                    : "Comece a assistir vídeos para ver seu histórico aqui"}
                </p>
              </CardContent>
            </Card>
          ) : (
            paginar(filteredAndSortedHistory).itens.map((item) => {
              const video = getVideo(item.videoId);
              const category = video ? getCategory(video.categoryId) : null;
              const fallback = videoProgressById[item.videoId];
              const effectiveDuration = (video && video.duration > 0) ? video.duration : (fallback?.duration || 0);
              const progressPercentage = effectiveDuration > 0
                ? Math.min((item.watchedDuration / effectiveDuration) * 100, 100)
                : (item.completed ? 100 : 0);

              if (!video) return null;

              return (
                <Card key={item.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row gap-4">
                      {/* Thumbnail */}
                      <div className="relative w-full md:w-48 h-32 bg-muted rounded-lg overflow-hidden flex-shrink-0">
                        {video.thumbnail ? (
                          <img
                            src={video.thumbnail}
                            alt={video.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Play className="h-8 w-8 text-muted-foreground" />
                          </div>
                        )}
                        <div className="absolute bottom-2 right-2 bg-background/90 px-2 py-1 rounded text-xs">
                          {formatDurationLong(video.duration)}
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 space-y-3">
                        <div>
                          <h3 className="text-lg font-semibold line-clamp-1">
                            {video.title}
                          </h3>
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {video.description}
                          </p>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          {category && (
                            <Badge variant="secondary">
                              {category.name}
                            </Badge>
                          )}
                          {/* módulo/submódulo */}
                          {(() => {
                            const catId = video.categoryId as string;
                            const list = modulesByCategory[catId] || [];
                            const mid = (video as any).moduleId || (video as any).module_id;
                            const mod = list.find((m: any) => m.id === mid);
                            return mod ? (
                              <Badge variant="outline">{mod.title}</Badge>
                            ) : null;
                          })()}
                          {item.completed ? (
                            <Badge className="bg-green-500/10 text-green-600 hover:bg-green-500/20">
                              Concluído
                            </Badge>
                          ) : (
                            <Badge variant="outline">
                              Em Progresso
                            </Badge>
                          )}
                          <Badge variant="outline" className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(item.lastWatchedAt)}
                          </Badge>
                        </div>

                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              {formatDurationLong(item.watchedDuration)} de {formatDurationLong(effectiveDuration || video.duration)}
                            </span>
                            <span className="font-medium">
                              {progressPercentage.toFixed(0)}%
                            </span>
                          </div>
                          <Progress value={progressPercentage} className="h-2" />
                        </div>

                        <Button
                          onClick={() => handleContinueWatching(video.id)}
                          className="w-full md:w-auto"
                        >
                          <Play className="h-4 w-4 mr-2" />
                          {item.completed ? "Assistir Novamente" : "Continuar Assistindo"}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}

          {paginar(filteredAndSortedHistory).total > 1 && (
            <div className="flex items-center justify-between pt-2 text-sm">
              <span className="text-muted-foreground">
                Página {paginar(filteredAndSortedHistory).atual} de {paginar(filteredAndSortedHistory).total}
                {' · '}{filteredAndSortedHistory.length} registro(s)
              </span>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm"
                        disabled={paginar(filteredAndSortedHistory).atual <= 1}
                        onClick={() => setPagina((p) => Math.max(1, p - 1))}>Anterior</Button>
                <Button variant="outline" size="sm"
                        disabled={paginar(filteredAndSortedHistory).atual >= paginar(filteredAndSortedHistory).total}
                        onClick={() => setPagina((p) => p + 1)}>Próxima</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}